// =============================================================================
// Error Boundary Component
// =============================================================================

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: true
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDevelopment = import.meta.env.DEV;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-2xl w-full space-y-6">
            {/* Error Icon */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Something went wrong
              </h1>
              <p className="text-muted-foreground text-lg">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="btn btn-default btn-md flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleReload}
                className="btn btn-outline btn-md flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="btn btn-ghost btn-md flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {/* Error Details (Development Only) */}
            {isDevelopment && error && (
              <div className="bg-card border rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">
                  Error Details (Development Mode)
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Error Message:
                    </h4>
                    <pre className="text-sm bg-muted p-3 rounded overflow-x-auto">
                      {error.toString()}
                    </pre>
                  </div>
                  
                  {errorInfo && errorInfo.componentStack && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Component Stack:
                      </h4>
                      <pre className="text-sm bg-muted p-3 rounded overflow-x-auto max-h-40">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                  
                  {error.stack && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Stack Trace:
                      </h4>
                      <pre className="text-sm bg-muted p-3 rounded overflow-x-auto max-h-60">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Help Text */}
            <div className="text-center text-sm text-muted-foreground">
              <p>
                If this problem persists, please{' '}
                <a 
                  href="mailto:support@ai-dev-workspace.com" 
                  className="text-primary hover:underline"
                >
                  contact support
                </a>
                {' '}or{' '}
                <a 
                  href="https://github.com/your-org/ai-dev-workspace/issues" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  report an issue
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
